/**
 * 通知先メール管理ページ (SBI-6.5)。
 *
 * 現場 WG の WebApp 上で、AlertRecipient テーブル (所属区所単位の通知メール宛先) を
 * 追加 / 削除する GUI。SBI-6 の CRUD API (`/alert-recipients`) を叩く。
 *
 * 機能:
 *   - 所属区所 (depot_id = 所属区所名。例: "さいたま車両") ごとに宛先をグルーピングして一覧表示
 *   - 追加フォーム: depot_id (select / depot-master.json の value + 既存登録値) + email + label(任意)
 *   - 各宛先行に削除ボタン (ConfirmDialog で確認 → DELETE)
 *   - 読み込み中 / エラー表示 / 再読み込み
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { AlertRecipientsClient } from '../api/alertRecipients';
import { ApiError } from '../api/common';
import type { AlertRecipient } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { AppConfig } from '../config';
import { extractDepotIds, fetchDepotMaster } from '../data/depots';
import { formatDateTime } from '../utils/dateTime';

interface AlertRecipientPageProps {
  config: AppConfig;
}

/** 削除確認ダイアログの対象 (depot_id + email) */
interface DeleteTarget {
  depot_id: string;
  email: string;
}

/** インライン編集中の行の状態 (depot_id は固定、email/label を編集) */
interface EditState {
  /** 編集対象の識別 (depot_id) */
  depot_id: string;
  /** 編集開始時の email (キー変更判定 / DELETE 対象に使う) */
  originalEmail: string;
  /** 編集開始時の label (変更判定に使う) */
  originalLabel: string;
  /** 入力中の email */
  email: string;
  /** 入力中の label */
  label: string;
  /** 保存処理中フラグ */
  saving: boolean;
}

/** 簡易メール形式チェック (バックエンドと同等の緩いチェック) */
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** 宛先一覧を depot_id ごとにグルーピングし、depot_id 昇順で返す。 */
function groupByDepot(
  items: AlertRecipient[],
): { depotId: string; recipients: AlertRecipient[] }[] {
  const map = new Map<string, AlertRecipient[]>();
  for (const item of items) {
    const list = map.get(item.depot_id) ?? [];
    list.push(item);
    map.set(item.depot_id, list);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([depotId, recipients]) => ({
      depotId,
      recipients: recipients.slice().sort((a, b) => a.email.localeCompare(b.email)),
    }));
}

/** API クライアントの例外を画面表示用文字列に整形する */
function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return `[HTTP ${err.status}] ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return '予期しないエラーが発生しました';
}

export function AlertRecipientPage(props: AlertRecipientPageProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new AlertRecipientsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [items, setItems] = useState<AlertRecipient[]>([]);
  const [masterDepotIds, setMasterDepotIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  // --- 追加フォームの入力状態 ---
  const [formDepotId, setFormDepotId] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // --- 削除確認ダイアログ ---
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  // --- インライン編集 ---
  const [edit, setEdit] = useState<EditState | null>(null);

  /** 宛先一覧を取得して state に反映する (競合防止に requestId を使う)。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      if (currentRequestId !== requestIdRef.current) return;
      setItems(res.items);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(formatApiError(err));
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  // 初回マウント時に depot マスタと宛先一覧を取得する。
  useEffect(() => {
    let depotMasterActive = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- API 取得の初回ロードは正当な副作用
    void reload();
    void fetchDepotMaster()
      .then((master) => {
        if (depotMasterActive) setMasterDepotIds(extractDepotIds(master));
      })
      // depot マスタ取得失敗は致命的ではない (自由入力で継続可能) ため握りつぶす
      .catch(() => {
        if (depotMasterActive) setMasterDepotIds([]);
      });
    return () => {
      depotMasterActive = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ。cleanup 時の最新値を加算して進行中の古い結果を破棄するのが意図
      ++requestIdRef.current;
    };
  }, [reload]);

  // depot_id の選択肢 = マスタ ∪ 既存登録済み depot_id (昇順ユニーク)
  const depotOptions = useMemo(() => {
    const set = new Set<string>(masterDepotIds);
    for (const item of items) set.add(item.depot_id);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [masterDepotIds, items]);

  const groups = useMemo(() => groupByDepot(items), [items]);

  const canSubmit =
    formDepotId.trim() !== '' && isValidEmail(formEmail) && !submitting && edit === null;

  /** 追加フォームの送信 (POST → リロード)。 */
  const handleAdd = useCallback(async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiClient.create({
        depot_id: formDepotId.trim(),
        email: formEmail.trim(),
        // label 省略 = put_item でクリア扱い (空文字時は意図的に省く)
        ...(formLabel.trim() ? { label: formLabel.trim() } : {}),
      });
      // 入力欄をクリア (depot_id は続けて追加しやすいよう残す)
      setFormEmail('');
      setFormLabel('');
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }, [apiClient, canSubmit, formDepotId, formEmail, formLabel, reload]);

  /** 削除確定 (DELETE → リロード)。 */
  const handleDeleteConfirmed = useCallback(async (): Promise<void> => {
    if (!deleteTarget || deleting) return;
    const target = deleteTarget;
    setDeleting(true);
    setErrorMessage(null);
    try {
      await apiClient.remove(target.depot_id, target.email);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      // 失敗時はダイアログを維持して再試行できるようにする
      setErrorMessage(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  }, [apiClient, deleteTarget, deleting, reload]);

  /** 指定行の編集を開始する。 */
  const startEdit = useCallback((r: AlertRecipient): void => {
    setErrorMessage(null);
    setEdit({
      depot_id: r.depot_id,
      originalEmail: r.email,
      originalLabel: r.label ?? '',
      email: r.email,
      label: r.label ?? '',
      saving: false,
    });
  }, []);

  /** 編集をキャンセルする。 */
  const cancelEdit = useCallback((): void => {
    setEdit(null);
  }, []);

  /** 削除確認ダイアログを閉じる。 */
  const cancelDelete = useCallback((): void => {
    setDeleteTarget(null);
  }, []);

  const canSaveEdit =
    edit !== null &&
    isValidEmail(edit.email) &&
    !edit.saving &&
    (edit.email.trim() !== edit.originalEmail.trim() ||
      edit.label.trim() !== edit.originalLabel.trim());

  /**
   * 編集を保存する。
   *
   * - email 未変更: create で上書き (label 更新)。
   * - email 変更あり: 新 email で create (追加) → 成功後に旧 email を remove (削除)。
   *   (email は SK のためキー変更は「追加 + 旧削除」で実現する)
   */
  const handleSaveEdit = useCallback(async (): Promise<void> => {
    if (edit === null || !isValidEmail(edit.email) || edit.saving) return;
    const target = edit;
    const newEmail = target.email.trim();
    const originalEmail = target.originalEmail.trim();
    const emailChanged = newEmail !== originalEmail;
    const labelChanged = target.label.trim() !== target.originalLabel.trim();
    if (!emailChanged && !labelChanged) {
      setEdit(null);
      return;
    }
    setEdit({ ...target, saving: true });
    setErrorMessage(null);
    try {
      await apiClient.create({
        depot_id: target.depot_id,
        email: newEmail,
        // label 省略 = put_item でクリア扱い (空文字時は意図的に省く)
        ...(target.label.trim() ? { label: target.label.trim() } : {}),
      });
    } catch (err) {
      setErrorMessage(formatApiError(err));
      setEdit((prev) => (prev ? { ...prev, saving: false } : prev));
      return;
    }
    if (emailChanged) {
      // 旧 email レコードを削除 (キー変更の後始末)
      try {
        await apiClient.remove(target.depot_id, originalEmail);
      } catch {
        // 新レコードは作成済み。実態を反映するため reload してから警告を表示する
        setEdit(null);
        await reload();
        setErrorMessage(
          `新しいメールアドレスの登録は完了しましたが、旧アドレス (${originalEmail}) の削除に失敗しました。画面を確認してください。`,
        );
        return;
      }
    }
    setEdit(null);
    await reload();
  }, [apiClient, edit, reload]);

  return (
    <section className="section" data-testid="alert-recipient-section">
      <h2>通知先メール管理{loading ? ' (読み込み中...)' : ` (${items.length} 件)`}</h2>

      {errorMessage ? (
        <div role="alert" className="error-message" data-testid="alert-recipient-error">
          {errorMessage}
        </div>
      ) : null}

      <form
        className="alert-recipient-form"
        data-testid="alert-recipient-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleAdd();
        }}
      >
        <label>
          所属区所
          <input
            type="text"
            list="depot-options"
            value={formDepotId}
            onChange={(e) => setFormDepotId(e.target.value)}
            placeholder="所属区所名（例: さいたま車両）"
            required
            data-testid="form-depot-id"
          />
          <datalist id="depot-options">
            {depotOptions.map((depotId) => (
              <option key={depotId} value={depotId} />
            ))}
          </datalist>
        </label>
        <label>
          メールアドレス
          <input
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="alert@example.com"
            required
            aria-invalid={formEmail !== '' && !isValidEmail(formEmail)}
            aria-describedby="form-email-hint"
            data-testid="form-email"
          />
          <span id="form-email-hint" className="field-hint">
            {formEmail !== '' && !isValidEmail(formEmail)
              ? '有効なメールアドレスを入力してください'
              : ''}
          </span>
        </label>
        <label>
          表示名 (任意)
          <input
            type="text"
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            placeholder="運用センター"
            data-testid="form-label"
          />
        </label>
        <button
          type="submit"
          className="primary"
          disabled={!canSubmit}
          data-testid="form-add-button"
        >
          追加
        </button>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={edit !== null}
          data-testid="alert-recipient-reload-button"
        >
          再読み込み
        </button>
      </form>

      {groups.length === 0 ? (
        <p className="data-table__empty" data-testid="alert-recipient-empty">
          {loading ? '読み込み中...' : '登録されている通知先がありません'}
        </p>
      ) : (
        groups.map((group) => (
          <div
            key={group.depotId}
            className="alert-recipient-group"
            data-testid={`depot-group-${encodeURIComponent(group.depotId)}`}
          >
            <h3>{group.depotId}</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">メールアドレス</th>
                    <th scope="col">表示名</th>
                    <th scope="col">更新日時</th>
                    <th scope="col">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {group.recipients.map((r) => {
                    const rowKey = `${encodeURIComponent(r.depot_id)}#${encodeURIComponent(r.email)}`;
                    const isEditing =
                      edit !== null &&
                      edit.depot_id === r.depot_id &&
                      edit.originalEmail === r.email;
                    if (isEditing && edit) {
                      return (
                        <tr key={rowKey} data-testid={`recipient-row-${rowKey}`}>
                          <td>
                            <input
                              type="email"
                              value={edit.email}
                              onChange={(e) =>
                                setEdit((prev) =>
                                  prev ? { ...prev, email: e.target.value } : prev,
                                )
                              }
                              aria-label={`${r.email} のメールアドレス(編集)`}
                              aria-invalid={!isValidEmail(edit.email)}
                              disabled={edit.saving}
                              data-testid={`edit-email-${rowKey}`}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={edit.label}
                              onChange={(e) =>
                                setEdit((prev) =>
                                  prev ? { ...prev, label: e.target.value } : prev,
                                )
                              }
                              aria-label={`${r.email} の表示名(編集)`}
                              placeholder="運用センター"
                              disabled={edit.saving}
                              data-testid={`edit-label-${rowKey}`}
                            />
                          </td>
                          <td>{formatDateTime(r.updated_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="primary"
                              onClick={() => void handleSaveEdit()}
                              disabled={!canSaveEdit}
                              data-testid={`save-button-${rowKey}`}
                            >
                              保存
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={edit.saving}
                              data-testid={`cancel-button-${rowKey}`}
                            >
                              キャンセル
                            </button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={rowKey} data-testid={`recipient-row-${rowKey}`}>
                        <td>{r.email}</td>
                        <td>{r.label ?? '—'}</td>
                        <td>{formatDateTime(r.updated_at)}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => startEdit(r)}
                            disabled={edit !== null}
                            aria-label={`${r.depot_id} ${r.email} を編集`}
                            data-testid={`edit-button-${rowKey}`}
                          >
                            編集
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() =>
                              setDeleteTarget({ depot_id: r.depot_id, email: r.email })
                            }
                            disabled={edit !== null}
                            aria-label={`${r.depot_id} ${r.email} を削除`}
                            data-testid={`delete-button-${rowKey}`}
                          >
                            削除
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="通知先メールの削除"
        message={
          deleteTarget
            ? `以下の通知先を削除します。よろしいですか？\n所属区所: ${deleteTarget.depot_id}\nメール: ${deleteTarget.email}`
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={deleting}
        cancelDisabled={deleting}
        onConfirm={() => void handleDeleteConfirmed()}
        onCancel={cancelDelete}
      />
    </section>
  );
}
